import React from 'react';
import { FileText, Send, Upload, Home as HomeIcon, Trophy, CalendarRange } from 'lucide-react';
import { motion } from 'framer-motion';
import { MetricTile } from './MetricTile';
import { Clock, Phone, PhoneCall } from 'lucide-react';
import { Loan, LoanStage, STAGE_POINTS } from '../data/loans';
import { OfficerData } from '../data/officers';
interface CustomRangeScorecardProps {
  startDate: string; // ISO yyyy-mm-dd
  endDate: string;
  loans: Loan[];
  officer: OfficerData;
  onTileClick?: (stage: LoanStage) => void;
  onSpeedBonusClick?: () => void;
  onAnswerRateClick?: () => void;
  onDialsClick?: () => void;
}
function formatDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function daysBetween(a: string, b: string) {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const start = new Date(ay, am - 1, ad).getTime();
  const end = new Date(by, bm - 1, bd).getTime();
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
}
export function CustomRangeScorecard({
  startDate,
  endDate,
  loans,
  officer,
  onTileClick,
  onSpeedBonusClick,
  onAnswerRateClick,
  onDialsClick
}: CustomRangeScorecardProps) {
  const LOANS = loans;
  // Aggregate events that fall inside [startDate, endDate]
  const inRange = (d: string) => d >= startDate && d <= endDate;
  const stageStats: Record<LoanStage, {
    count: number;
    points: number;
  }> = {
    credit_pull: {
      count: 0,
      points: 0
    },
    doc_out: {
      count: 0,
      points: 0
    },
    submission: {
      count: 0,
      points: 0
    },
    closing: {
      count: 0,
      points: 0
    }
  };
  LOANS.forEach(loan => {
    loan.events.forEach(e => {
      if (inRange(e.date)) {
        stageStats[e.stage].count += 1;
        stageStats[e.stage].points += e.points;
      }
    });
  });
  const totalPoints = Object.values(stageStats).reduce((s, v) => s + v.points, 0);
  const totalActions = Object.values(stageStats).reduce((s, v) => s + v.count, 0);
  const days = daysBetween(startDate, endDate);
  const dailyAvg = Math.round(totalPoints / days);
  // Speed bonus loans whose closing date falls within the range
  const speedBonusLoans = LOANS.filter(loan => {
    if (!loan.speedBonus || loan.speedBonus <= 0) return false;
    const closing = loan.events.find(e => e.stage === 'closing');
    return closing ? inRange(closing.date) : false;
  });
  const speedBonusPts = speedBonusLoans.reduce((s, l) => s + (l.speedBonus ?? 0), 0);
  const invalid = startDate > endDate;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" data-id="element-292" data-mp="2ju6ohgjg6wja">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between gap-3 text-white" data-id="element-293" data-mp="y38gaj3niom6">
        <div className="flex items-center gap-2 min-w-0" data-id="element-294" data-mp="131s4kq5pwchp">
          <CalendarRange className="w-5 h-5 shrink-0" data-id="element-295" data-mp="132bhfsgigaov" />
          <h2 className="font-bold tracking-wide truncate" data-id="element-296" data-mp="173djeh1tb0uj">CUSTOM RANGE</h2>
        </div>
        <span className="text-xs sm:text-sm font-medium text-blue-200 shrink-0" data-id="element-297" data-mp="1c39gdwc3ds0l">
          {formatDate(startDate)} – {formatDate(endDate)}
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-298" data-mp="1h00495u51rwk">
        {invalid ? <div className="text-center py-10 text-gray-500" data-id="element-299" data-mp="1lzcrhrox90zo">
            <p className="font-bold text-red-600 mb-1" data-id="element-300" data-mp="1qr4b1i9nu6gb">Invalid date range</p>
            <p className="text-sm" data-id="element-301" data-mp="1ut7uou6hghco">
              Start date must be on or before the end date.
            </p>
          </div> : <>
            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-gray-200 gap-y-4 mb-6" data-id="element-302" data-mp="1zqhm2fdfn335">
              <SummaryStat label="Total Points" sub="In Range" value={totalPoints.toLocaleString()} valueClass="text-[#0a1e5e]" data-id="element-303" data-mp="1r4oohusynptm" />
              <SummaryStat label="Actions" sub="Logged" value={totalActions.toString()} valueClass="text-[#0a1e5e]" data-id="element-304" data-mp="20u88j3setpq2" />
              <SummaryStat label="Days" sub="In Range" value={days.toString()} valueClass="text-[#0a1e5e]" data-id="element-305" data-mp="185u528ijy174" />
              <SummaryStat label="Daily Avg" sub="Pts / Day" value={dailyAvg.toLocaleString()} valueClass="text-green-600" data-id="element-306" data-mp="1393u1zlltvsk" />
            </div>

            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-307" data-mp="2tix3el2vgytd">
              Range Breakdown
            </h3>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-id="element-308" data-mp="2ojape9f7zeji">
              <MetricTile title="Call Answer Rate" points={0} count={officer.callAnswerRate} countSuffix="%" countLabel="Answer Rate" icon={Phone} colorClass="text-teal-600" bgClass="bg-teal-100" hidePoints onClick={onAnswerRateClick} data-id="element-309" data-mp="2w3brn5pxuwv5" />
              <MetricTile title="Dials" points={0} count={officer.dials} countLabel="Dials" icon={PhoneCall} colorClass="text-indigo-600" bgClass="bg-indigo-100" hidePoints onClick={onDialsClick} data-id="element-310" data-mp="3ev4aw8f7h3po" />
              <MetricTile title="Credit Pulls" points={STAGE_POINTS.credit_pull} count={stageStats.credit_pull.count} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" ptsPerAction={STAGE_POINTS.credit_pull} onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-311" data-mp="1nkqa5cj60pn" />
              <MetricTile title="Doc Outs" points={STAGE_POINTS.doc_out} count={stageStats.doc_out.count} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" ptsPerAction={STAGE_POINTS.doc_out} onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-312" data-mp="2aqsk9fiemyi" />
              <MetricTile title="Submissions" points={STAGE_POINTS.submission} count={stageStats.submission.count} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" ptsPerAction={STAGE_POINTS.submission} onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-313" data-mp="3fqy8xj1ag22i" />
              <MetricTile title="Closings" points={STAGE_POINTS.closing} count={stageStats.closing.count} countLabel="Closings" icon={HomeIcon} colorClass="text-orange-500" bgClass="bg-orange-100" ptsPerAction={STAGE_POINTS.closing} onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-314" data-mp="35m7z6qzn7tnq" />
            </div>

            {/* Speed Bonus in range */}
            {speedBonusLoans.length > 0 && <button type="button" onClick={onSpeedBonusClick} disabled={!onSpeedBonusClick} aria-label="View speed bonus loans in range" className={`group relative w-full bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3 mb-4 transition-all text-left ${onSpeedBonusClick ? 'hover:border-orange-400 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400' : 'cursor-default'}`} data-id="element-315" data-mp="1u58ae5ci7wyg">
                <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-316" data-mp="52lgxfpv42w">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-317" data-mp="37yldkvx1w6">
                    <Clock className="w-6 h-6 text-white" data-id="element-318" data-mp="9v49jp82kmht" />
                  </div>
                  <div className="min-w-0" data-id="element-319" data-mp="9x8fsdtfxxl9">
                    <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-320" data-mp="4vreih71o945">
                      Speed Bonus in Range
                    </p>
                    <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-321" data-mp="jqxiyzqhrzvd">
                      Close a loan within 14 days of credit pull
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0" data-id="element-322" data-mp="12hlylj2439ul">
                  <div className="flex items-baseline gap-1 justify-end" data-id="element-323" data-mp="12h2dxul2u7cv">
                    <span className="text-2xl font-extrabold text-orange-500 leading-none" data-id="element-324" data-mp="ogrkcjsdueo0">
                      +{speedBonusPts.toLocaleString()}
                    </span>
                    <span className="text-sm font-bold text-gray-500" data-id="element-325" data-mp="es9o8fv1svqp">PTS</span>
                  </div>
                  <p className="text-[10px] font-bold text-[#0a1e5e] uppercase mt-1" data-id="element-326" data-mp="jj9l5l463dt7">
                    {speedBonusLoans.length} Fast{' '}
                    {speedBonusLoans.length === 1 ? 'Close' : 'Closes'}
                  </p>
                </div>
                {onSpeedBonusClick && <span className="absolute top-2 right-2 text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider" data-id="element-327" data-mp="3ryt6zz2idty8">
                    View loans →
                  </span>}
              </button>}

            {/* Total banner */}
            <div className="mt-auto bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-id="element-328" data-mp="2v4581o0ljgd1">
              <div className="w-12 h-12 bg-[#0a1e5e] rounded-full flex items-center justify-center shrink-0" data-id="element-329" data-mp="2g74qgdc4d1sa">
                <Trophy className="w-6 h-6 text-yellow-400" data-id="element-330" data-mp="2l5fzilulb89g" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-331" data-mp="38vjte1hz7tn9">
                <p className="text-xs font-bold text-gray-500 uppercase leading-tight" data-id="element-332" data-mp="38wuwdz3lkxdb">
                  Total Points in Range
                </p>
                <div className="flex items-baseline gap-1 flex-wrap" data-id="element-333" data-mp="2z3ntqm08rvgx">
                  <span className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-334" data-mp="343ilerrucp31">
                    {totalPoints.toLocaleString()}
                  </span>
                  <span className="text-xs font-bold text-gray-500" data-id="element-335" data-mp="3rr9vdopwsq15">
                    POINTS
                  </span>
                </div>
              </div>
              {totalActions === 0 && <span className="text-xs font-semibold text-gray-400 shrink-0" data-id="element-336" data-mp="1l313q25kiq4j">
                  No activity in this range
                </span>}
            </div>
          </>}
      </div>
    </motion.div>;
}
function SummaryStat({
  label,
  sub,
  value,
  valueClass
}: {
  label: string;
  sub: string;
  value: string;
  valueClass: string;
}) {
  return <div className="text-center px-2 min-w-0" data-id="element-337" data-mp="126rrzli52bnp">
      <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-338" data-mp="x7excel100ak">
        {label}
      </p>
      <p className={`text-2xl sm:text-3xl font-extrabold leading-none ${valueClass}`} data-id="element-339" data-mp="1b76ir0dvgiyl">
        {value}
      </p>
      <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-340" data-mp="173rp36x0g42o">
        {sub}
      </p>
    </div>;
}